import { Box, Button, Typography } from "@mui/material";
import DashboardNoFindingSvg from "~/components/icons/mondoo/DashboardNoFindingSvg";
import { useLoadSpaceQuery } from "~/operations/types";
import { TopActionsCardSkeleton } from "./TopActionsCardSkeleton";
import { Flex } from "~/components/Flex";
import { Link as RouterLink } from "react-router-dom";
import { getToSpaceSettings } from "~/lib/urlUtils";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { ReactNode } from "react";
import { VulnerabilitiesIcon } from "~/icons";

interface Props {
  space: SpaceOrWorkspaceScope;
  totalFindings: number;
  isSpaceDashboard: boolean;
}

const TopActionsCardEmptyState = ({
  space,
  totalFindings,
  isSpaceDashboard,
}: Props) => {
  const { data, loading } = useLoadSpaceQuery({
    variables: { spaceMrn: space.mrn },
  });

  const settings = data?.space?.settings;
  const isPlatformVulnerabilityConfigurationDisabled =
    !settings?.platformVulnerabilityConfiguration?.enable;

  const getNoFindingsTitle = (): string => {
    if (isPlatformVulnerabilityConfigurationDisabled) {
      return "Vulnerability scanning is disabled";
    }

    if (totalFindings > 0) {
      return "No advisories found";
    }

    return "No security findings found";
  };

  const getIcon = (): ReactNode => {
    if (isPlatformVulnerabilityConfigurationDisabled) {
      return (
        <Box
          sx={{
            marginTop: isSpaceDashboard ? 1.1 : 4.6,
            "& svg": {
              width: 36,
              height: 36,
            },
          }}
        >
          <VulnerabilitiesIcon
            sx={{ color: (theme) => theme.palette.text.disabled }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          marginTop: isSpaceDashboard ? 4.7 : 8.1,
          "& svg": {
            width: 36,
            height: 36,
            color: "text.secondary",
          },
        }}
      >
        <DashboardNoFindingSvg />
      </Box>
    );
  };

  const getNoFindingsDescription = (): ReactNode => {
    if (isPlatformVulnerabilityConfigurationDisabled) {
      return (
        <Flex flexDirection="column" gap={1} alignItems="center">
          <Typography
            variant="body1"
            sx={{
              fontWeight: "medium",
              color: "text.secondary",
              textAlign: "center",
              fontSize: { xs: "14px", sm: "16px" },
              px: { xs: 1, sm: 0 },
            }}
          >
            Vulnerability scanning is currently disabled. To enable
            vulnerability scanning, turn it on in the space settings.
          </Typography>

          <Button
            component={RouterLink}
            to={getToSpaceSettings(space.id)}
            variant="outlined"
            color="primary"
            sx={{ mt: 1 }}
          >
            Go to space settings
          </Button>
        </Flex>
      );
    }

    if (totalFindings === 0) {
      return (
        <Typography
          variant="body1"
          sx={{
            fontWeight: "medium",
            color: "text.secondary",
            textAlign: "center",
            fontSize: { xs: "14px", sm: "16px" },
            px: { xs: 1, sm: 0 },
            mb: 3,
          }}
        >
          This space has been scanned for known advisories.
          <br />
          It is currently not affected by any of them.
        </Typography>
      );
    }

    return (
      <Typography
        variant="body1"
        sx={{
          fontWeight: "medium",
          color: "text.secondary",
          textAlign: "center",
          fontSize: { xs: "14px", sm: "16px" },
          px: { xs: 1, sm: 0 },
          mb: 3,
        }}
      >
        This space has been scanned for known advisories.
        <br />
        It is currently not affected by any of them.
      </Typography>
    );
  };

  if (loading) {
    return <TopActionsCardSkeleton />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100% - 24px)",
        gap: { xs: 2, sm: 1 },
        p: { xs: 2, sm: 3 },
        textAlign: "center",
      }}
    >
      {getIcon()}

      <Box
        sx={{
          maxWidth: {
            xs: "100%",
            sm: 420,
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "700",
            fontSize: { xs: "16px", sm: "24px" },
            mb: { xs: 1, sm: 1.5 },
          }}
        >
          {getNoFindingsTitle()}
        </Typography>

        {getNoFindingsDescription()}
      </Box>
    </Box>
  );
};

export default TopActionsCardEmptyState;
